import React, { useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDown, ChevronRight, Folder, FolderOpen, FolderPlus, Layers, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Modal } from '@components/Dialog';
import documentService from '@services/documentService';
import type { DocumentFolder } from '@/types/document';

interface FolderPanelProps {
  folders: DocumentFolder[];
  /** `null` = todas as pastas. */
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  /** Recarrega a árvore depois de criar/renomear/excluir. */
  onChanged: () => void;
  onError: (err: unknown) => void;
  canWrite: boolean;
  /** Total de documentos (linha "Todas as pastas"). */
  totalCount?: number;
}

type Editor = { mode: 'create'; parent: DocumentFolder | null } | { mode: 'rename'; folder: DocumentFolder };

/** Árvore de pastas (1 nível + subpastas) com criar / renomear / excluir. Pastas de sistema só renomeiam. */
export const FolderPanel: React.FC<FolderPanelProps> = ({ folders, selectedId, onSelect, onChanged, onError, canWrite, totalCount }) => {
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [editor, setEditor] = useState<Editor | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<DocumentFolder | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const openCreate = (parent: DocumentFolder | null) => {
    setName('');
    setEditor({ mode: 'create', parent });
  };
  const openRename = (folder: DocumentFolder) => {
    setName(folder.name);
    setEditor({ mode: 'rename', folder });
  };

  const save = async () => {
    if (!editor || !name.trim()) return;
    setSaving(true);
    try {
      if (editor.mode === 'create') {
        await documentService.createFolder({ name: name.trim(), parent_folder_id: editor.parent?.id ?? null });
      } else {
        await documentService.updateFolder(editor.folder.id, {
          name: name.trim(),
          parent_folder_id: editor.folder.parent_folder_id ?? null,
          sort_order: editor.folder.sort_order,
        });
      }
      setEditor(null);
      onChanged();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await documentService.deleteFolder(deleting.id);
      if (selectedId === deleting.id) onSelect(null);
      setDeleting(null);
      onChanged();
    } catch (err) {
      setDeleting(null);
      onError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const row = (f: DocumentFolder, depth: number) => {
    const children = f.children ?? [];
    const hasChildren = children.length > 0;
    const isCollapsed = !!collapsed[f.id];
    const active = selectedId === f.id;
    return (
      <div key={f.id}>
        <div
          className={clsx(
            'group flex items-center gap-1 rounded-lg pr-1 text-sm',
            active ? 'bg-emerald-100 text-emerald-800' : 'text-slate-700 hover:bg-slate-100',
          )}
          style={{ paddingLeft: `${depth * 14}px` }}
        >
          <button
            type="button"
            className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-0"
            disabled={!hasChildren}
            onClick={() => setCollapsed((c) => ({ ...c, [f.id]: !isCollapsed }))}
            aria-label={isCollapsed ? 'Expandir' : 'Recolher'}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </button>
          <button type="button" onClick={() => onSelect(f.id)} className="flex-1 min-w-0 flex items-center gap-2 py-1.5 text-left">
            {active ? <FolderOpen size={16} className="flex-shrink-0" /> : <Folder size={16} className="flex-shrink-0 text-slate-400" />}
            <span className="truncate">{f.name}</span>
            {f.is_system && <Lock size={11} className="text-slate-300 flex-shrink-0" aria-label="Pasta de sistema" />}
            <span className="ml-auto text-xs text-slate-400 tabular-nums pl-2">{f.document_count}</span>
          </button>
          {canWrite && (
            <div className="hidden group-hover:flex items-center">
              {depth === 0 && (
                <button type="button" title="Nova subpasta" className="p-1 text-slate-400 hover:text-emerald-700" onClick={() => openCreate(f)}>
                  <FolderPlus size={14} />
                </button>
              )}
              <button type="button" title="Renomear" className="p-1 text-slate-400 hover:text-emerald-700" onClick={() => openRename(f)}>
                <Pencil size={14} />
              </button>
              {!f.is_system && (
                <button type="button" title="Excluir" className="p-1 text-slate-400 hover:text-red-600" onClick={() => setDeleting(f)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          )}
        </div>
        {hasChildren && !isCollapsed && <div>{children.map((c) => row(c, depth + 1))}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-label">Pastas</h2>
        {canWrite && (
          <button type="button" onClick={() => openCreate(null)} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1">
            <Plus size={12} /> Nova pasta
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={clsx(
          'w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left',
          selectedId === null ? 'bg-emerald-100 text-emerald-800' : 'text-slate-700 hover:bg-slate-100',
        )}
      >
        <Layers size={16} className={selectedId === null ? '' : 'text-slate-400'} />
        <span className="flex-1">Todas as pastas</span>
        {totalCount !== undefined && <span className="text-xs text-slate-400 tabular-nums">{totalCount}</span>}
      </button>
      {folders.map((f) => row(f, 0))}
      {folders.length === 0 && <p className="text-small px-2 py-2">Nenhuma pasta.</p>}

      {editor && (
        <Modal isOpen onClose={() => !saving && setEditor(null)} size="sm" closeOnBackdrop={!saving}>
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-h3">
              {editor.mode === 'create' ? (editor.parent ? `Nova subpasta em "${editor.parent.name}"` : 'Nova pasta') : 'Renomear pasta'}
            </h2>
          </div>
          <form
            className="px-6 py-4"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <label className="text-label block mb-1">
              Nome <span className="text-red-500">*</span>
            </label>
            <input className="input-field" autoFocus maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
            {editor.mode === 'rename' && editor.folder.is_system && (
              <p className="text-small mt-2">Pasta de sistema: pode ser renomeada, mas não excluída.</p>
            )}
          </form>
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditor(null)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={!name.trim()}>
              {editor.mode === 'create' ? 'Criar' : 'Salvar'}
            </Button>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir pasta"
        message={`Excluir a pasta "${deleting?.name ?? ''}"? Só é possível excluir pastas vazias (sem documentos e sem subpastas).`}
        confirmText="Excluir"
        severity="danger"
        loading={deleteBusy}
      />
    </div>
  );
};
